import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { getMapCenter } from '@/lib/map/instance';
import { revealCurrentSearchArea } from '@/lib/map/layers/oiSearchArea';
import { SEARCH_SIZE_KM, clampSizeKm } from '@/lib/oi/searchArea';
import { useOiStore } from '@/lib/store/oi';
import { LocateFixed, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const COMMIT_DEBOUNCE_MS = 400;

/** ENG-189 search bar + ENG-193/207 search-area row (3rd Party only). */
export function OiSearchHeader() {
  const query = useOiStore((s) => s.searchQuery);
  const setQuery = useOiStore((s) => s.setSearchQuery);
  const category = useOiStore((s) => s.category);
  return (
    <>
      <div className="relative mt-2">
        <Search
          size={12}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 opacity-40"
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search OIs"
          placeholder="Search"
          className="h-11 w-full rounded-md border border-(--color-border) bg-(--color-bg) py-[5px] pl-7 pr-7 text-[0.769em] text-(--color-text) outline-none focus:border-(--color-green) lg:h-auto"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer rounded p-0.5 text-(--color-text3) hover:bg-(--color-surface) hover:text-(--color-text)"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {category === '3rdparty' && <SearchAreaControls />}
    </>
  );
}

function SearchAreaControls() {
  const sizeKm = useOiStore((s) => s.searchArea.sizeKm);
  const setSearchArea = useOiStore((s) => s.setSearchArea);
  const [draft, setDraft] = useState(String(sizeKm));

  // The box can be resized on the map — mirror the committed value.
  useEffect(() => {
    setDraft(String(sizeKm));
  }, [sizeKm]);

  // Debounced commit only for in-range values; anything else waits for
  // blur / Enter, which clamps (so typing "4" on the way to "40" never
  // snaps the input).
  useEffect(() => {
    const n = Number.parseFloat(draft);
    if (!Number.isFinite(n) || n < SEARCH_SIZE_KM.min || n > SEARCH_SIZE_KM.max) return;
    if (clampSizeKm(n) === sizeKm) return;
    const id = window.setTimeout(() => setSearchArea({ sizeKm: n }), COMMIT_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [draft, sizeKm, setSearchArea]);

  const commit = () => {
    const n = Number.parseFloat(draft);
    if (!Number.isFinite(n)) {
      setDraft(String(sizeKm));
      return;
    }
    const next = clampSizeKm(n);
    setSearchArea({ sizeKm: next });
    setDraft(String(next));
  };

  return (
    <div className="mt-2 flex items-center gap-3">
      <InputGroup className="h-11 w-[84px] lg:h-8">
        <InputGroupInput
          type="number"
          inputMode="numeric"
          min={SEARCH_SIZE_KM.min}
          max={SEARCH_SIZE_KM.max}
          aria-label="Search area size in kilometres"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
        />
        <InputGroupAddon>km</InputGroupAddon>
      </InputGroup>
      <button
        type="button"
        onClick={() => {
          setSearchArea({ center: getMapCenter() });
          revealCurrentSearchArea();
        }}
        className="inline-flex h-11 items-center gap-1.5 text-[0.769em] font-semibold text-(--color-text) hover:text-(--color-green) lg:h-8"
      >
        <LocateFixed size={16} className="text-(--color-green)" />
        Set Focus
      </button>
    </div>
  );
}
